/**
 * Phase 2B.2 — Editor support components: Breadcrumb + Undo/Redo hook.
 *
 *   A2 (Undo/Redo): useEditorHistory — ring-buffer history stack. Phase 2B.2
 *      ships the architecture + keyboard hooks (Cmd+Z / Cmd+Shift+Z). Full
 *      structural undo across autosave round-trips lands in Phase 2C.
 *   A3 (Breadcrumb): EditorBreadcrumb — Assessment › Section › Content path.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Undo2, Redo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HISTORY_CAP = 50;

/* ============================================================================
 *                              A3 — Breadcrumb
 * ========================================================================== */
export function EditorBreadcrumb({ assetName, section, content }) {
  const { t } = useTranslation();
  const crumbs = [{ label: assetName || t('studio.breadcrumb.assessment', { defaultValue: 'Assessment' }), key: 'asset' }];
  if (section) crumbs.push({ label: section.name || t('studio.breadcrumb.untitledSection', { defaultValue: 'Untitled section' }), key: 'section' });
  if (content) crumbs.push({ label: shortPrompt(content.prompt) || t('studio.breadcrumb.untitledContent', { defaultValue: 'Untitled content' }), key: 'content', stable: content.stable_id });
  return (
    <nav
      aria-label={t('studio.breadcrumb.label', { defaultValue: 'Editor breadcrumb' })}
      className="flex items-center gap-1 text-xs text-muted-foreground min-w-0 flex-1"
      data-testid="studio-editor-breadcrumb"
    >
      {crumbs.map((c, i) => (
        <React.Fragment key={c.key}>
          {i > 0 && <ChevronRight className="h-3 w-3 shrink-0 opacity-60" />}
          <span
            data-testid={`studio-breadcrumb-${c.key}`}
            className={`truncate ${i === crumbs.length - 1 ? 'text-foreground font-medium' : ''}`}
          >
            {c.stable && (
              <span className="font-mono text-xs mr-1 px-1 py-0.5 rounded bg-muted/60 text-foreground/70" data-testid="studio-breadcrumb-stable-id">
                {c.stable}
              </span>
            )}
            {c.label}
          </span>
        </React.Fragment>
      ))}
    </nav>
  );
}

function shortPrompt(p) {
  if (!p) return null;
  const s = String(p).trim();
  return s.length > 40 ? `${s.slice(0, 37)}…` : s;
}

/* ============================================================================
 *                              A2 — Undo/Redo hook
 * ========================================================================== */
/**
 * useEditorHistory — minimal ring-buffer that captures snapshots of editor
 * state. The current Phase 2B.2 implementation only captures FE-side state
 * (versions array). Phase 2C will extend this to coordinate with autosave
 * snapshots so undo crosses round-trips.
 *
 * Returns:
 *   - push(snapshot): record a new state.
 *   - undo(): pop one step back; returns the snapshot or null.
 *   - redo(): step forward; returns the snapshot or null.
 *   - canUndo / canRedo flags.
 */
export function useEditorHistory(initialSnapshot) {
  const stackRef = useRef(initialSnapshot ? [JSON.stringify(initialSnapshot)] : []);
  const indexRef = useRef(initialSnapshot ? 0 : -1);
  const [, force] = useState(0);

  const push = useCallback((snapshot) => {
    if (snapshot == null) return;
    const json = typeof snapshot === 'string' ? snapshot : JSON.stringify(snapshot);
    // Discard any redo branch.
    stackRef.current = stackRef.current.slice(0, indexRef.current + 1);
    stackRef.current.push(json);
    if (stackRef.current.length > HISTORY_CAP) {
      stackRef.current.shift();
    } else {
      indexRef.current += 1;
    }
    force((n) => n + 1);
  }, []);

  const undo = useCallback(() => {
    if (indexRef.current <= 0) return null;
    indexRef.current -= 1;
    force((n) => n + 1);
    try { return JSON.parse(stackRef.current[indexRef.current]); }
    catch { return null; }
  }, []);

  const redo = useCallback(() => {
    if (indexRef.current >= stackRef.current.length - 1) return null;
    indexRef.current += 1;
    force((n) => n + 1);
    try { return JSON.parse(stackRef.current[indexRef.current]); }
    catch { return null; }
  }, []);

  const canUndo = indexRef.current > 0;
  const canRedo = indexRef.current < stackRef.current.length - 1;
  return { push, undo, redo, canUndo, canRedo };
}

/* ============================================================================
 *                       UndoRedoButtons — top-bar control
 * ========================================================================== */
export function UndoRedoButtons({ canUndo, canRedo, onUndo, onRedo }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-0.5" data-testid="studio-undo-redo">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        disabled={!canUndo}
        onClick={onUndo}
        data-testid="studio-undo-button"
        title={t('studio.editor.undoTooltip', { defaultValue: 'Undo (Cmd+Z)' })}
      >
        <Undo2 className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        disabled={!canRedo}
        onClick={onRedo}
        data-testid="studio-redo-button"
        title={t('studio.editor.redoTooltip', { defaultValue: 'Redo (Cmd+Shift+Z)' })}
      >
        <Redo2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/* ============================================================================
 *               useEditorHotkeys — Cmd+Z / Cmd+Shift+Z plumbing
 * ========================================================================== */
export function useEditorHotkeys({ onUndo, onRedo }) {
  useEffect(() => {
    const handler = (e) => {
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) onRedo?.();
        else onUndo?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onUndo, onRedo]);
}
